import { useEffect } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import EpisodesSection from './components/EpisodesSection';
import Newsletter from './components/Newsletter';
import ShopSection from './components/ShopSection';
import ToolBench from './components/ToolBench';
import BeerMap from './components/BeerMap';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://beerafterwork.com';

const HOME_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      'url': SITE_URL,
      'name': 'Beer After Work',
      'description': 'DIY repair YouTube channel with craft beer rewards. Fix things, drink beer — because you have to fix it for the biscuit.',
      'potentialAction': {
        '@type': 'SearchAction',
        'target': {
          '@type': 'EntryPoint',
          'urlTemplate': `${SITE_URL}/?s={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      },
      'sameAs': [
        'https://www.youtube.com/@beerafterwork'
      ]
    },
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/#webpage`,
      'url': SITE_URL,
      'name': 'Beer After Work – DIY Fixes & Craft Beer | Fix it for the Biscuit',
      'description': 'Beer After Work: DIY repairs, garage fixes, and craft beer rewards. Fix things, drink beer — because you have to fix it for the biscuit.',
      'isPartOf': { '@id': `${SITE_URL}/#website` },
      'dateModified': '2026-03-25',
      'inLanguage': 'en-US',
      'breadcrumb': {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': 'Home',
            'item': SITE_URL
          }
        ]
      }
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      'name': 'Beer After Work',
      'url': SITE_URL,
      'description': 'A YouTube channel dedicated to DIY repairs, garage fixes, and craft beer rewards. Every fix earns a cold pint.',
      'sameAs': [
        'https://www.youtube.com/@beerafterwork'
      ]
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What is Beer After Work?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Beer After Work is a YouTube channel about DIY repairs and garage fixes. After every successful fix, the host rewards themselves with a craft beer — because you have to fix it for the biscuit.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What is the Biscuit Brief newsletter?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The Biscuit Brief is a weekly email newsletter delivered every Friday, featuring one tricky fix explained, one craft beer recommendation, and one community project shoutout.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Where can I find the tools used on Beer After Work?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The Tool Bench section of the website lists all the affiliate tools used on the channel, including power tools, hand tools, diagnostics equipment, and specialty garage gear.'
          }
        }
      ]
    },
    {
      '@type': 'ItemList',
      'name': 'Beer After Work – Recent DIY Repair Episodes',
      'description': 'A playlist of the latest Beer After Work YouTube episodes featuring DIY repairs, garage fixes, and craft beer rewards.',
      'url': 'https://www.youtube.com/@beerafterwork/videos',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'item': {
            '@type': 'VideoObject',
            'name': 'EP. 24 – 1987 Ford F-150 Carburetor Rebuild',
            'description': 'Full carburetor rebuild on a 1987 Ford F-150. Three stripped bolts, one snapped fuel line, and a 47-year-old carb that had never been touched. Beer earned: Midnight Oil Oatmeal Stout.',
            'thumbnailUrl': 'https://img.youtube.com/vi/TItZpIUpevI/maxresdefault.jpg',
            'uploadDate': '2026-03-14',
            'contentUrl': 'https://www.youtube.com/watch?v=TItZpIUpevI',
            'embedUrl': 'https://www.youtube.com/embed/TItZpIUpevI',
            'publisher': { '@id': `${SITE_URL}/#organization` }
          }
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'item': {
            '@type': 'VideoObject',
            'name': 'EP. 23 – Welding a Cracked Lawn Tractor Frame',
            'description': 'Welding repairs on a cracked riding mower frame. Structural weld, grind and paint. Beer earned: Rustbelt Amber Ale from Great Lakes Brewing Co.',
            'thumbnailUrl': 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
            'uploadDate': '2026-03-01',
            'contentUrl': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
            'embedUrl': 'https://www.youtube.com/embed/dQw4w9WgXcQ',
            'publisher': { '@id': `${SITE_URL}/#organization` }
          }
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'item': {
            '@type': 'VideoObject',
            'name': 'EP. 22 – Seized Brake Caliper Restoration',
            'description': 'Fully seized brake caliper restored to working condition. Rust, corrosion, and brake dust everywhere. Beer earned: Double Clutch IPA from Founders Brewing Co.',
            'thumbnailUrl': 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
            'uploadDate': '2026-02-15',
            'contentUrl': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
            'embedUrl': 'https://www.youtube.com/embed/dQw4w9WgXcQ',
            'publisher': { '@id': `${SITE_URL}/#organization` }
          }
        },
        {
          '@type': 'ListItem',
          'position': 4,
          'item': {
            '@type': 'VideoObject',
            'name': 'EP. 21 – Dead Riding Mower Ignition Fix',
            'description': 'Corroded ignition switch on a riding mower diagnosed and replaced. Fixed for $18 off eBay. Beer earned: Yard Work Wheat Beer from Bell\'s Brewery.',
            'thumbnailUrl': 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
            'uploadDate': '2026-02-02',
            'contentUrl': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
            'embedUrl': 'https://www.youtube.com/embed/dQw4w9WgXcQ',
            'publisher': { '@id': `${SITE_URL}/#organization` }
          }
        },
        {
          '@type': 'ListItem',
          'position': 5,
          'item': {
            '@type': 'VideoObject',
            'name': 'EP. 20 – Rusted Exhaust Manifold Removal',
            'description': 'Two broken studs, one drill bit casualty, and a near-wiring-loom disaster. The hardest exhaust job on the channel. Beer earned: Ironworks Porter from Dogfish Head.',
            'thumbnailUrl': 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
            'uploadDate': '2026-01-20',
            'contentUrl': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
            'embedUrl': 'https://www.youtube.com/embed/dQw4w9WgXcQ',
            'publisher': { '@id': `${SITE_URL}/#organization` }
          }
        },
        {
          '@type': 'ListItem',
          'position': 6,
          'item': {
            '@type': 'VideoObject',
            'name': 'EP. 19 – Generator Carburetor Deep Clean',
            'description': 'Varnished jets from old fuel sitting all winter. Spray cleaner, compressed air, copper wire. Generator runs better than new. Beer earned: Off-Grid Session Lager from Sierra Nevada.',
            'thumbnailUrl': 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
            'uploadDate': '2026-01-08',
            'contentUrl': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
            'embedUrl': 'https://www.youtube.com/embed/dQw4w9WgXcQ',
            'publisher': { '@id': `${SITE_URL}/#organization` }
          }
        }
      ]
    }
  ]
};

const HomePage = () => {
  useEffect(() => {
    document.title = 'Beer After Work – Fix Things. Drink Beer.';

    const scriptId = 'home-schema-jsonld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(HOME_SCHEMA);

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#111111] overflow-x-hidden">
      <Navbar />

      <main>
        <section>
          <HeroSection />
        </section>

        <section>
          <EpisodesSection />
        </section>

        <section>
          <Newsletter />
        </section>

        <section>
          <ShopSection />
        </section>

        <section>
          <ToolBench />
        </section>

        <section>
          <BeerMap />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
